import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Wallet } from 'lucide-react';

const DEFAULT_CURRENCY = '₴';

const ShopElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onItemDrag = null,          // (itemId, {x, y}) → parent editor persists new position
  onWalletDrag = null,        // ({x, y}) → parent editor persists wallet widget position
  onPurchasedChange = null,   // (purchasedIds[]) → lesson sync
  syncedPurchasedIds = null,  // array of purchased item ids from server (student sync)
  disabled = false,
}) => {
  const { content = {} } = element;
  const {
    totalBudget = 100,
    currency = DEFAULT_CURRENCY,
    items = [],
    tagBorder = '#2A4D25',
    tagText = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 16,
    walletX = 8,
    walletY = 5,
  } = content;

  const isControlled = Array.isArray(syncedPurchasedIds);
  const [localPurchased, setLocalPurchased] = useState([]);
  const [flashId, setFlashId] = useState(null);        // shake tag red when unaffordable
  const flashTimer = useRef(null);
  const containerRef = useRef(null);

  const purchasedIds = isControlled ? syncedPurchasedIds : localPurchased;

  // Reset local state when the underlying items change (e.g. new session).
  const itemIdsKey = items.map((i) => i.id).join('|');
  useEffect(() => {
    if (!isControlled) setLocalPurchased([]);
  }, [itemIdsKey, isControlled]);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const spent = useMemo(
    () => items
      .filter((i) => purchasedIds.includes(i.id))
      .reduce((sum, i) => sum + (Number(i.price) || 0), 0),
    [items, purchasedIds]
  );
  const remaining = Math.max(0, Number(totalBudget) - spent);

  const flashUnaffordable = (id) => {
    setFlashId(id);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashId(null), 450);
  };

  const handleTagClick = (item) => {
    if (isEditor || disabled || !canInteract) return;
    const already = purchasedIds.includes(item.id);
    const price = Number(item.price) || 0;
    let next;
    if (already) {
      next = purchasedIds.filter((id) => id !== item.id);
    } else {
      if (price > remaining) {
        flashUnaffordable(item.id);
        return;
      }
      next = [...purchasedIds, item.id];
    }
    if (isControlled) {
      if (onPurchasedChange) onPurchasedChange(next);
    } else {
      setLocalPurchased(next);
      if (onPurchasedChange) onPurchasedChange(next);
    }
  };

  // Editor drag: pointer-events to move price tags within the container.
  // Coordinates stored as percentage of the container to survive resize.
  const dragState = useRef(null);
  const handleTagPointerDown = (e, item) => {
    if (!isEditor || !onItemDrag) return;
    e.stopPropagation();
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return;
    dragState.current = {
      itemId: item.id,
      boxLeft: box.left,
      boxTop: box.top,
      boxW: box.width,
      boxH: box.height,
      startX: e.clientX,
      startY: e.clientY,
      origX: item.x,
      origY: item.y,
      moved: false,
    };
    const onMove = (ev) => {
      const st = dragState.current;
      if (!st) return;
      const dx = ((ev.clientX - st.startX) / st.boxW) * 100;
      const dy = ((ev.clientY - st.startY) / st.boxH) * 100;
      if (Math.abs(ev.clientX - st.startX) > 2 || Math.abs(ev.clientY - st.startY) > 2) {
        st.moved = true;
      }
      const nx = Math.max(0, Math.min(100, st.origX + dx));
      const ny = Math.max(0, Math.min(100, st.origY + dy));
      onItemDrag(st.itemId, { x: nx, y: ny });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragState.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const walletDragState = useRef(null);
  const handleWalletPointerDown = (e) => {
    if (!isEditor || !onWalletDrag) return;
    e.stopPropagation();
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return;
    walletDragState.current = {
      boxW: box.width,
      boxH: box.height,
      startX: e.clientX,
      startY: e.clientY,
      origX: walletX,
      origY: walletY,
    };
    const onMove = (ev) => {
      const st = walletDragState.current;
      if (!st) return;
      const dx = ((ev.clientX - st.startX) / st.boxW) * 100;
      const dy = ((ev.clientY - st.startY) / st.boxH) * 100;
      const nx = Math.max(0, Math.min(100, st.origX + dx));
      const ny = Math.max(0, Math.min(100, st.origY + dy));
      onWalletDrag({ x: nx, y: ny });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      walletDragState.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const overBudget = spent > Number(totalBudget);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      style={{ overflow: 'hidden' }}
    >
      <style>{`
        @keyframes shop-shake {
          0%, 100% { transform: translate(-50%, -50%); }
          25% { transform: translate(calc(-50% - 6px), -50%); }
          50% { transform: translate(calc(-50% + 6px), -50%); }
          75% { transform: translate(calc(-50% - 4px), -50%); }
        }
        .shop-shake { animation: shop-shake 0.4s ease-in-out; }
      `}</style>

      {/* Wallet / budget widget: shows spent/total */}
      <div
        onPointerDown={handleWalletPointerDown}
        className={`absolute z-10 flex items-center gap-2 px-3 py-1.5 rounded-full shadow-md select-none ${isEditor && onWalletDrag ? '' : 'pointer-events-none'}`}
        style={{
          top: `${walletY}%`,
          left: `${walletX}%`,
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #d1d5db',
          fontFamily,
          cursor: isEditor && onWalletDrag ? 'move' : 'default',
        }}
      >
        <Wallet size={16} className="text-emerald-700" />
        <span
          className="font-bold leading-none tabular-nums"
          style={{
            fontSize: `${Math.max(14, fontSize)}px`,
            color: overBudget ? '#b91c1c' : (remaining === 0 ? '#9a3412' : '#065f46'),
          }}
        >
          {currency}{spent}
          <span className="text-gray-400 font-normal">/</span>
          {currency}{totalBudget}
        </span>
      </div>

      {/* Price tags */}
      {items.map((item) => {
        const purchased = purchasedIds.includes(item.id);
        const flashing = flashId === item.id;
        const price = Number(item.price) || 0;
        const label = (item.label || '').trim();
        return (
          <div
            key={item.id}
            onClick={() => handleTagClick(item)}
            onPointerDown={(e) => handleTagPointerDown(e, item)}
            className={`absolute flex items-center gap-2 select-none ${flashing ? 'shop-shake' : ''}`}
            style={{
              left: `${item.x}%`,
              top: `${item.y}%`,
              transform: 'translate(-50%, -50%)',
              cursor: isEditor ? 'move' : (canInteract && !disabled ? 'pointer' : 'default'),
              transition: flashing ? 'none' : 'opacity 150ms, transform 150ms',
              zIndex: purchased ? 3 : 2,
              opacity: purchased ? 0.85 : 1,
              fontFamily,
            }}
          >
            {label && (
              <span
                className="font-medium"
                style={{
                  color: tagText,
                  fontSize: `${fontSize}px`,
                }}
              >
                {label}
              </span>
            )}
            <span
              className="font-bold px-3 py-1 rounded-full text-white shadow-md"
              style={{
                background: purchased ? '#16a34a' : (flashing ? '#dc2626' : tagBorder),
                fontSize: `${fontSize}px`,
              }}
            >
              {currency}{price}
            </span>
          </div>
        );
      })}

      {/* Editor empty-state hint */}
      {isEditor && items.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="px-4 py-2 rounded-lg bg-black/60 text-white text-xs font-medium shadow">
            Додай цінники у панелі властивостей праворуч
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopElement;
